/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './UploadFileToFunction';

const UploadFileToFunctionManager: ComponentManagerFn<
  'UploadFileToFunction'
> = (
  platform: Platform,
  type: 'UploadFileToFunction',
): ComponentManager<'UploadFileToFunction'> => {
  return {
    ...baseComponentManager<'UploadFileToFunction'>(
      platform,
      'UploadFileToFunction',
    ),
    type: 'UploadFileToFunction',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnResponse', 'ActionSetParam'] as ItemType[]).includes(
        childType,
      ),
    defaultValue,
    props: {
      functionUri: {
        label: 'Function',
        type: 'FunctionURIChooser',
        required: true,
        refreshCallback(_cb) {
          // no support for different app
        },
        getAppUid(page, item, context) {
          // use the same app as the page
          return undefined;
        },
      },
      dropzoneText: {
        label: 'Dropzone Text',
        type: 'string',
      },
      images: {
        label: 'Images',
        type: 'boolean',
        helpText: 'Check this to accept only image types.',
      },
      accept: {
        label: 'Accept',
        type: 'string',
        helpText: `Specify comma separated list of types to accept. eg. .pdf, .png, .jpeg.

Refer this for more info [react-dropzone](https://react-dropzone.js.org/#section-accepting-specific-file-types)`,
      },
    },
    getDisplayText: (item) => item.props.functionUri ?? '',
    name: 'Upload File To Function',
    category: 'Forms',
    defaultChildren: ['ActionSetParam', 'ActionSetParam', 'TriggerOnResponse'],
    icon: { iconName: 'upload' },
    description: `Use this to upload files to a cloud function. This is useful when you want to store the uploaded file in a database or send it to any external service.

Use the **Set Param** to set the parameters to be sent to the function. All the parameters will be sent as a JSON object that can be access as \`request.body.params\`.

**Example Function**

\`\`\`js
// file is the uploaded file (b"<base64 encoded file content>")
// myVar1, myVar2 are the values set in the Set Param component
const { file, fileName, contentType, fileSize, params: {myVar1, myVar2} } = request.body;

const fileId = await db.queryNumber("SELECT FND_LOBS_S.NEXTVAL FROM DUAL");

await db.executeUpdate(\`insert into FND_LOBS(FILE_DATA,FILE_FORMAT,UPLOAD_DATE,FILE_ID,FILE_NAME,FILE_CONTENT_TYPE) values (:1,'binary',sysdate,:2,:3,:4)\`, [file, fileId, fileName, contentType]);

// fileId, fileName, contentType will be available in the Trigger On Response. They can be accessed as $._.response.fileId, $._.response.fileName, $._.response.contentType
response.json({ fileId, fileName, contentType });
\`\`\`
`,
  };
};

export default UploadFileToFunctionManager;
